import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { continentsAPI, recipesAPI, statsAPI } from '../lib/api';
import { logger } from '../lib/logger';
import { Navbar } from '../components/layout/Navbar';
import { ContinentCard } from '../components/continents/ContinentCard';
import { RecipeCard } from '../components/recipes/RecipeCard';
import { RecipeOfTheDay } from '../components/recipes/RecipeOfTheDay';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Globe, Users, Sparkles, ArrowRight, Utensils } from 'lucide-react';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1699572583203-97170ae281c0?w=1600&q=80';

export default function HomePage() {
  const [continents, setContinents] = useState([]);
  const [recentRecipes, setRecentRecipes] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [continentsRes, recipesRes, statsRes] = await Promise.all([
          continentsAPI.getAll(),
          recipesAPI.getAll({ limit: 6 }),
          statsAPI.get(),
        ]);
        setContinents(continentsRes.data);
        setRecentRecipes(recipesRes.data);
        setStats(statsRes.data);
      } catch (error) {
        logger.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Cuisine du monde"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-worldplate-deep/90 via-worldplate-deep/70 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-worldplate-accent font-accent text-2xl mb-4">
              Explorez les saveurs du monde
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
              La cuisine du monde à portée de main
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed">
              Découvrez des milliers de recettes authentiques venues des quatre coins du globe. 
              Voyagez depuis votre cuisine.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-3 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Rechercher une recette, un ingrédient..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg rounded-full border-2 border-transparent bg-white focus:border-worldplate-primary"
                  data-testid="hero-search-input"
                />
              </div>
              <Button 
                type="submit"
                className="h-14 px-8 rounded-full bg-worldplate-primary hover:bg-worldplate-primary/90 text-white font-bold"
                data-testid="hero-search-btn"
              >
                Rechercher
              </Button>
            </form>

            {/* Quick Stats */}
            {stats && (
              <div className="flex flex-wrap gap-6 text-white/90">
                <div className="flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-worldplate-accent" />
                  <span>{stats.total_recipes} recettes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-worldplate-accent" />
                  <span>6 continents</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-worldplate-accent" />
                  <span>{stats.total_users} chefs</span>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Recipe of the Day */}
      <RecipeOfTheDay />

      {/* Continents Section */}
      <section className="py-20 md:py-32 bg-worldplate-cream" data-testid="continents-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <span className="text-worldplate-primary font-accent text-xl">Voyagez par</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-worldplate-deep mt-2">
              Continent
            </h2>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-3xl bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {continents.map((continent, index) => (
                <ContinentCard key={continent.id} continent={continent} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Recent Recipes Section */}
      {recentRecipes.length > 0 && (
        <section className="py-20 md:py-32 bg-worldplate-subtle" data-testid="recent-recipes-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-end justify-between mb-12"
            >
              <div>
                <span className="text-worldplate-secondary font-accent text-xl">Dernières</span>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-worldplate-deep mt-2">
                  Recettes ajoutées
                </h2>
              </div>
              <Link to="/search">
                <Button variant="ghost" className="text-worldplate-primary hover:text-worldplate-primary/80 gap-2" data-testid="see-all-recipes-btn">
                  Voir tout
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentRecipes.map((recipe, index) => (
                <RecipeCard key={recipe.id} recipe={recipe} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-worldplate-deep" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 bg-worldplate-primary/20 text-worldplate-accent px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">Propulsé par l'IA</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Générez des recettes avec l'IA
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
              Choisissez un pays et un type de plat, et notre IA vous génère une recette authentique 
              en quelques secondes.
            </p>
            <Link to="/recipes/new">
              <Button className="bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-full px-8 py-6 text-lg font-bold" data-testid="generate-recipe-cta-btn">
                Essayer maintenant
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-worldplate-cream border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-1">
              <img src="/logo.png" alt="Globe Gourmand" className="h-16 w-auto" />
            </div>
            <p className="text-worldplate-muted text-sm">
              © 2024 Globe Gourmand. Fait avec amour pour les gourmands du monde entier.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
